import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject
} from 'react'

import { Button } from '../../atoms/button/button'
import { Input } from '../input/input'

interface PaginationJumpProps {
  containerRef: RefObject<HTMLElement>
  label: string
  currentPage: number
  totalPages: number
  disabled: boolean
  className: string
  onPageChange: (page: number) => void
}

export const PaginationJump = ({
  containerRef,
  label,
  currentPage,
  totalPages,
  disabled,
  className,
  onPageChange
}: PaginationJumpProps) => {
  const [draft, setDraft] = useState<string | null>(null)
  const [hasError, setHasError] = useState(false)
  const restoreFocus = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const descriptionId = useId()

  const handleInputRef = useCallback(
    (input: HTMLInputElement | null) => {
      const previous = inputRef.current
      // A prop change can remove the focused input or its entire ellipsis.
      if (
        !input &&
        previous &&
        previous.ownerDocument.activeElement === previous
      ) {
        containerRef.current?.focus()
      }
      inputRef.current = input
    },
    [containerRef]
  )

  const handleTriggerRef = useCallback((button: HTMLButtonElement | null) => {
    if (button && restoreFocus.current) {
      restoreFocus.current = false
      button.focus()
    }
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return
    if (event.key !== 'Enter' && event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()

    if (event.key === 'Escape') {
      restoreFocus.current = true
      setDraft(null)
      return
    }

    const value = draft?.trim() ?? ''
    const target = Number(value)
    if (!/^\d+$/.test(value) || target < 1 || target > totalPages) {
      setHasError(true)
      return
    }

    // The destination can remove this ellipsis, so focus the stable navigation
    // region before the parent changes the page (including async consumers).
    containerRef.current?.focus()
    setDraft(null)
    if (target !== currentPage) onPageChange(target)
  }

  if (draft === null) {
    return (
      <Button
        ref={handleTriggerRef}
        variant='secondary'
        size='sm'
        className={className}
        data-slot='pagination-ellipsis'
        aria-label={label}
        disabled={disabled}
        onClick={() => {
          setHasError(false)
          setDraft('')
        }}
      >
        <span aria-hidden='true'>...</span>
      </Button>
    )
  }

  return (
    <span
      className='text-control-lg relative inline-flex'
      style={{ width: `${Math.max(4, String(totalPages).length + 2)}ch` }}
    >
      <Input
        ref={handleInputRef}
        autoFocus
        size='sm'
        className='rounded-button-sm h-8 px-1'
        inputClassName='text-control-lg min-w-0 text-center font-semibold'
        aria-label='Page number'
        aria-describedby={descriptionId}
        inputMode='numeric'
        autoComplete='off'
        value={draft}
        error={hasError}
        onChange={event => {
          setDraft(event.target.value)
          setHasError(false)
        }}
        onKeyDownCapture={handleKeyDown}
        onBlur={() => setDraft(null)}
      />
      <span id={descriptionId} className='sr-only'>
        Enter a page from 1 to {totalPages}. Press Enter to go or Escape to
        cancel.
      </span>
      {hasError && (
        <span
          role='alert'
          onPointerDown={event => event.preventDefault()}
          className='bg-surface text-danger-icon shadow-overlay absolute left-0 top-full z-10 mt-1 w-max max-w-56 rounded px-2 py-1 text-sm/5'
        >
          Enter a whole page number from 1 to {totalPages}.
        </span>
      )}
    </span>
  )
}
